import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Persons from "../AdminPerson/Persons";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";

function money(value) {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return "0.00";
  return n.toFixed(2);
}

function getDocumentoText(option) {
  if (!option) return "";

  const documentos = [];

  if (option.cedula) {
    documentos.push(`Cédula: ${option.cedula}`);
  }

  if (option.ruc) {
    documentos.push(`RUC: ${option.ruc}`);
  }

  return documentos.join(" | ");
}

function getPersonName(option) {
  if (!option) return "";
  return option.razon_social || option.nombre_comercial || "";
}

function getPersonLabel(option) {
  if (!option) return "";

  const documento = getDocumentoText(option);
  const name = getPersonName(option);

  if (documento && name) return `${documento} - ${name}`;
  if (documento) return documento;
  return name;
}

function resolveDefaultDocumento(persona) {
  if (!persona) {
    return {
      documento_tipo: "",
      documento_valor: "",
    };
  }

  if (persona.documento_tipo && persona.documento_valor) {
    return {
      documento_tipo: persona.documento_tipo,
      documento_valor: persona.documento_valor,
    };
  }

  if (persona.cedula) {
    return {
      documento_tipo: "CEDULA",
      documento_valor: persona.cedula,
    };
  }

  if (persona.ruc) {
    return {
      documento_tipo: "RUC",
      documento_valor: persona.ruc,
    };
  }

  return {
    documento_tipo: "",
    documento_valor: "",
  };
}

function fieldSx(dashboardTheme) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      color: dashboardTheme.text_color,
      bgcolor: dashboardTheme.input_background || dashboardTheme.card_background,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: dashboardTheme.primary_color,
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.text_color,
    },
    "& input": {
      color: dashboardTheme.text_color,
    },
    "& textarea": {
      color: dashboardTheme.text_color,
    },
  };
}

function Row({ label, children, dashboardTheme }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "230px minmax(0, 1fr)" },
        borderBottom: `1px solid ${dashboardTheme.border_color}`,
      }}
    >
      <Box
        sx={{
          bgcolor: dashboardTheme.theme_mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(17,24,39,0.035)",
          px: { xs: 1.5, md: 2 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: dashboardTheme.text_color,
          }}
        >
          {label}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1.2, md: 1.4 },
          py: 1,
          display: "flex",
          alignItems: "center",
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function DetailInvoice({
  orden,
  invoiceData,
  updateInvoice,
  personOptions,
  personLoading,
  personInput,
  setPersonInput,
  establecimientos,
  availablePuntos,
  formasPago,
  documentoFactura,
  resumenFactura,
  onPersonCreated,
}) {
  const { dashboardTheme } = useDashboardTheme();
  const [personModalOpen, setPersonModalOpen] = useState(false);

  const tipoPersonaInitialValues = useMemo(() => {
    return {
      tipo: "N",
      es_cliente: invoiceData.tipo_persona === "CLIENTE",
      es_proveedor: invoiceData.tipo_persona === "PROVEEDOR",
      es_empleado: false,
      es_extranjero: false,
    };
  }, [invoiceData.tipo_persona]);

  const handlePersonSaved = (data) => {
    const persona = data?.persona || data?.person || data?.result || data?.data || data;

    if (persona?.id) {
      const documento = resolveDefaultDocumento(persona);

      updateInvoice({
        persona,
        ...documento,
      });

      onPersonCreated?.(persona);
    }

    setPersonModalOpen(false);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          border: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
          overflow: "hidden",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.4,
            borderBottom: `1px solid ${dashboardTheme.border_color}`,
            bgcolor: dashboardTheme.theme_mode === "dark" ? "rgba(255,255,255,0.025)" : "rgba(17,24,39,0.02)",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 23 },
              fontWeight: 700,
              color: dashboardTheme.primary_color,
              lineHeight: 1.2,
            }}
          >
            Información del Documento
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <Box
            sx={{
              borderTop: `1px solid ${dashboardTheme.border_color}`,
              borderLeft: `1px solid ${dashboardTheme.border_color}`,
              borderRight: `1px solid ${dashboardTheme.border_color}`,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Row label="Fecha de emisión" dashboardTheme={dashboardTheme}>
              <TextField
                size="small"
                type="date"
                value={invoiceData.fecha_emision}
                onChange={(event) => updateInvoice({ fecha_emision: event.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: { xs: "100%", md: 340 },
                  ...fieldSx(dashboardTheme),
                }}
              />
            </Row>

            <Row label="Tipo de persona" dashboardTheme={dashboardTheme}>
              <TextField
                select
                size="small"
                value={invoiceData.tipo_persona}
                onChange={(event) => {
                  updateInvoice({
                    tipo_persona: event.target.value,
                    persona: null,
                    documento_tipo: "",
                    documento_valor: "",
                  });
                  setPersonInput("");
                }}
                sx={{
                  width: { xs: "100%", md: 340 },
                  ...fieldSx(dashboardTheme),
                }}
              >
                <MenuItem value="CLIENTE">Cliente</MenuItem>
                <MenuItem value="PROVEEDOR">Proveedor</MenuItem>
              </TextField>
            </Row>

            <Row label="Establecimiento" dashboardTheme={dashboardTheme}>
              <TextField
                select
                size="small"
                value={invoiceData.establecimiento}
                onChange={(event) => {
                  updateInvoice({
                    establecimiento: event.target.value,
                    punto_emision_id: "",
                  });
                }}
                sx={{
                  width: { xs: "100%", md: 340 },
                  ...fieldSx(dashboardTheme),
                }}
              >
                {establecimientos.map((item) => (
                  <MenuItem key={item.id} value={item.codigo}>
                    {item.codigo} - {item.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Row>

            <Row label="Punto de emisión" dashboardTheme={dashboardTheme}>
              <TextField
                select
                size="small"
                value={invoiceData.punto_emision_id}
                onChange={(event) => updateInvoice({ punto_emision_id: event.target.value })}
                sx={{
                  width: { xs: "100%", md: 340 },
                  ...fieldSx(dashboardTheme),
                }}
              >
                {availablePuntos.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.establecimiento}-{item.punto_emision}
                  </MenuItem>
                ))}
              </TextField>
            </Row>

            <Row label="Número de documento" dashboardTheme={dashboardTheme}>
              <TextField
                size="small"
                value={documentoFactura || ""}
                InputProps={{ readOnly: true }}
                sx={{
                  width: { xs: "100%", md: 340 },
                  ...fieldSx(dashboardTheme),
                }}
              />
            </Row>

            <Row label="Descripción factura" dashboardTheme={dashboardTheme}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                value={invoiceData.descripcion || ""}
                onChange={(event) => updateInvoice({ descripcion: event.target.value })}
                placeholder="Descripción general de la factura"
                sx={fieldSx(dashboardTheme)}
              />
            </Row>

            <Row label="Persona" dashboardTheme={dashboardTheme}>
              <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={personOptions}
                  loading={personLoading}
                  value={invoiceData.persona}
                  inputValue={personInput}
                  onInputChange={(_event, newInputValue) => setPersonInput(newInputValue)}
                  onChange={(_event, newValue) => {
                    const documento = resolveDefaultDocumento(newValue);

                    updateInvoice({
                      persona: newValue,
                      ...documento,
                    });
                  }}
                  getOptionLabel={getPersonLabel}
                  isOptionEqualToValue={(option, selected) => {
                    const optionKey = option?.option_uid || option?.id;
                    const selectedKey = selected?.option_uid || selected?.id;
                    return String(optionKey) === String(selectedKey);
                  }}
                  renderOption={(props, option) => {
                    const key = option.option_uid || option.id;

                    return (
                      <Box component="li" {...props} key={key}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: dashboardTheme.text_color,
                              lineHeight: 1.25,
                            }}
                          >
                            {getPersonName(option) || "-"}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.25,
                              fontSize: 12.5,
                              color: dashboardTheme.muted_text_color,
                              lineHeight: 1.25,
                            }}
                          >
                            {getDocumentoText(option) || "Sin documento"}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={invoiceData.tipo_persona === "CLIENTE" ? "Buscar cliente..." : "Buscar proveedor..."}
                      sx={fieldSx(dashboardTheme)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {personLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  slotProps={{
                    paper: {
                      sx: {
                        bgcolor: dashboardTheme.card_background,
                        color: dashboardTheme.text_color,
                        border: `1px solid ${dashboardTheme.border_color}`,
                        "& .MuiAutocomplete-option": {
                          color: dashboardTheme.text_color,
                        },
                        "& .MuiAutocomplete-option.Mui-focused": {
                          bgcolor: dashboardTheme.theme_mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(17,24,39,0.06)",
                        },
                      },
                    },
                  }}
                />

                <IconButton
                  onClick={() => setPersonModalOpen(true)}
                  sx={{
                    minWidth: 42,
                    width: 42,
                    height: 40,
                    borderRadius: "10px",
                    color: dashboardTheme.button_text_color,
                    bgcolor: dashboardTheme.button_color,
                    "&:hover": {
                      bgcolor: dashboardTheme.button_hover_color,
                    },
                  }}
                >
                  <AddRoundedIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Stack>
            </Row>
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: "14px",
          border: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
          overflow: "hidden",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.4,
            borderBottom: `1px solid ${dashboardTheme.border_color}`,
            bgcolor: dashboardTheme.theme_mode === "dark" ? "rgba(255,255,255,0.025)" : "rgba(17,24,39,0.02)",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 23 },
              fontWeight: 700,
              color: dashboardTheme.primary_color,
              lineHeight: 1.2,
            }}
          >
            Información de Cobro
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <Box
            sx={{
              borderTop: `1px solid ${dashboardTheme.border_color}`,
              borderLeft: `1px solid ${dashboardTheme.border_color}`,
              borderRight: `1px solid ${dashboardTheme.border_color}`,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Row label="Total" dashboardTheme={dashboardTheme}>
              <TextField
                size="small"
                value={`$${money(resumenFactura?.total)}`}
                InputProps={{ readOnly: true }}
                sx={{
                  width: { xs: "100%", md: 340 },
                  ...fieldSx(dashboardTheme),
                }}
              />
            </Row>

            <Row label="Forma de pago" dashboardTheme={dashboardTheme}>
              <TextField
                select
                size="small"
                value={invoiceData.forma_pago}
                onChange={(event) => {
                  updateInvoice({
                    forma_pago: event.target.value,
                    valor_recibido: resumenFactura?.total || invoiceData.valor_recibido,
                  });
                }}
                sx={{
                  width: { xs: "100%", md: 340 },
                  ...fieldSx(dashboardTheme),
                }}
              >
                {formasPago.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Row>

            <Row label="Valor recibido" dashboardTheme={dashboardTheme}>
              <TextField
                size="small"
                type="number"
                value={invoiceData.valor_recibido}
                onChange={(event) => updateInvoice({ valor_recibido: event.target.value })}
                disabled={invoiceData.forma_pago === "TRANSFERENCIA"}
                inputProps={{ step: "any" }}
                sx={{
                  width: { xs: "100%", md: 340 },
                  ...fieldSx(dashboardTheme),
                }}
              />
            </Row>

            {invoiceData.forma_pago === "TRANSFERENCIA" && (
              <Row label="Código transferencia" dashboardTheme={dashboardTheme}>
                <TextField
                  size="small"
                  value={invoiceData.transferencia_codigo}
                  onChange={(event) => updateInvoice({ transferencia_codigo: event.target.value })}
                  sx={{
                    width: { xs: "100%", md: 340 },
                    ...fieldSx(dashboardTheme),
                  }}
                />
              </Row>
            )}
          </Box>
        </Box>
      </Paper>

      <Persons
        open={personModalOpen}
        person={null}
        initialValues={tipoPersonaInitialValues}
        onClose={() => setPersonModalOpen(false)}
        onSaved={handlePersonSaved}
      />
    </>
  );
}